// 📄 pages/MiEspacio/components/MiEspacioPinGate.tsx
// Bloqueo opcional de "Mi Espacio" con un PIN personal — a pedido del
// usuario (Admin de plataforma) para proteger sus notas/código Z en caso
// de que alguien más use su sesión ya abierta. NO es una traba extra sobre
// los datos en Firestore (esos ya son privados por uid, ver
// miEspacioService.ts) — es fricción de INTERFAZ: cada vez que se entra a
// Mi Espacio (sin persistir entre navegaciones, a pedido explícito), hay
// que tipear el PIN antes de ver el contenido. El PIN se verifica 100%
// server-side (ver miEspacioSeguridadService.ts / miEspacioPinService.js),
// nunca se compara en el cliente.
import React, { useEffect, useState } from 'react';
import { EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { Lock, ShieldCheck, ShieldOff, RefreshCw, KeyRound } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext.tsx';
import {
  obtenerEstadoPinMiEspacio, guardarPinMiEspacio, verificarPinMiEspacio, eliminarPinMiEspacio
} from '../../../services/miEspacioSeguridadService.ts';

const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500";

export default function MiEspacioPinGate({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [cargando, setCargando] = useState(true);
  const [tienePin, setTienePin] = useState(false);
  const [desbloqueado, setDesbloqueado] = useState(false);

  const [configurando, setConfigurando] = useState(false);
  const [olvideModoReauth, setOlvideModoReauth] = useState(false);

  useEffect(() => {
    obtenerEstadoPinMiEspacio()
      .then(({ tienePin }) => setTienePin(tienePin))
      .catch((err) => console.error('Error consultando el estado del PIN de Mi Espacio:', err))
      .finally(() => setCargando(false));
  }, []);

  if (cargando) {
    return (
      <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!tienePin) {
    return (
      <div className="space-y-4">
        <div className="flex justify-end">
          <button
            onClick={() => setConfigurando(true)}
            className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-blue-400 transition-colors"
          >
            <Lock className="w-3.5 h-3.5" /> Proteger con PIN
          </button>
        </div>
        {children}
        {configurando && (
          <ConfigurarPinModal
            onClose={() => setConfigurando(false)}
            onGuardado={() => { setConfigurando(false); setTienePin(true); setDesbloqueado(true); }}
          />
        )}
      </div>
    );
  }

  if (!desbloqueado) {
    return (
      <PantallaBloqueo
        userEmail={user?.email || ''}
        onDesbloqueado={() => setDesbloqueado(true)}
        onOlvide={() => setOlvideModoReauth(true)}
        olvideModoReauth={olvideModoReauth}
        onCerrarOlvide={() => setOlvideModoReauth(false)}
        onPinReemplazado={() => { setOlvideModoReauth(false); setDesbloqueado(true); }}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          onClick={async () => {
            if (!window.confirm('¿Quitar la protección con PIN de Mi Espacio?')) return;
            try {
              await eliminarPinMiEspacio();
              setTienePin(false);
            } catch (err) {
              console.error('Error quitando el PIN de Mi Espacio:', err);
            }
          }}
          className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-red-400 transition-colors"
        >
          <ShieldOff className="w-3.5 h-3.5" /> Quitar PIN
        </button>
      </div>
      {children}
    </div>
  );
}

function PantallaBloqueo({
  userEmail, onDesbloqueado, onOlvide, olvideModoReauth, onCerrarOlvide, onPinReemplazado
}: {
  userEmail: string; onDesbloqueado: () => void; onOlvide: () => void;
  olvideModoReauth: boolean; onCerrarOlvide: () => void; onPinReemplazado: () => void;
}) {
  const { user } = useAuth();
  const [pin, setPin] = useState('');
  const [verificando, setVerificando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [passwordActual, setPasswordActual] = useState('');
  const [reautenticando, setReautenticando] = useState(false);
  const [errorReauth, setErrorReauth] = useState<string | null>(null);
  const [reautenticado, setReautenticado] = useState(false);

  const handleVerificar = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setVerificando(true);
    try {
      const valido = await verificarPinMiEspacio(pin);
      if (valido) onDesbloqueado();
      else setError('PIN incorrecto.');
    } catch (err: any) {
      setError(err.message || 'Error al verificar el PIN.');
    } finally {
      setVerificando(false);
    }
  };

  const handleReautenticar = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorReauth(null);
    if (!user?.email) { setErrorReauth('Esta cuenta no tiene email asociado.'); return; }

    setReautenticando(true);
    try {
      const credential = EmailAuthProvider.credential(user.email, passwordActual);
      await reauthenticateWithCredential(user, credential);
      setReautenticado(true);
    } catch (err: any) {
      if (err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password') {
        setErrorReauth('La contraseña no es correcta.');
      } else {
        setErrorReauth(err.message || 'Error al verificar tu contraseña.');
      }
    } finally {
      setReautenticando(false);
    }
  };

  return (
    <div className="max-w-sm mx-auto mt-16 bg-white/5 border border-white/10 rounded-2xl p-6 space-y-4 text-center">
      <Lock className="w-8 h-8 mx-auto text-blue-400" />
      <div>
        <h2 className="text-base font-semibold text-white">Mi Espacio está protegido</h2>
        <p className="text-xs text-gray-500 mt-1">{userEmail}</p>
      </div>

      {!olvideModoReauth ? (
        <>
          <form onSubmit={handleVerificar} className="space-y-3">
            {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-2.5 rounded-lg text-xs">{error}</div>}
            <input
              type="password" autoFocus value={pin} onChange={(e) => setPin(e.target.value)}
              placeholder="PIN" className={`${inputClass} text-center tracking-widest`}
            />
            <button
              type="submit"
              disabled={verificando || !pin}
              className="w-full flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all"
            >
              {verificando ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              Desbloquear
            </button>
          </form>
          <button onClick={onOlvide} className="text-xs text-gray-500 hover:text-gray-300">¿Olvidaste tu PIN?</button>
        </>
      ) : !reautenticado ? (
        <form onSubmit={handleReautenticar} className="space-y-3">
          <p className="text-xs text-gray-400">Confirmá tu contraseña de OneHub para definir un PIN nuevo.</p>
          {errorReauth && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-2.5 rounded-lg text-xs">{errorReauth}</div>}
          <input
            type="password" autoFocus value={passwordActual} onChange={(e) => setPasswordActual(e.target.value)}
            placeholder="Tu contraseña" className={inputClass}
          />
          <button
            type="submit"
            disabled={reautenticando || !passwordActual}
            className="w-full flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all"
          >
            {reautenticando ? <RefreshCw className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
            Confirmar
          </button>
          <button type="button" onClick={onCerrarOlvide} className="text-xs text-gray-500 hover:text-gray-300 block w-full">Cancelar</button>
        </form>
      ) : (
        <NuevoPinForm onGuardado={onPinReemplazado} />
      )}
    </div>
  );
}

function NuevoPinForm({ onGuardado }: { onGuardado: () => void }) {
  const [pin, setPin] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setGuardando(true);
    try {
      await guardarPinMiEspacio(pin);
      onGuardado();
    } catch (err: any) {
      setError(err.message || 'Error al guardar el PIN.');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <p className="text-xs text-gray-400">Definí tu PIN nuevo (mínimo 4 caracteres).</p>
      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-2.5 rounded-lg text-xs">{error}</div>}
      <input
        type="password" autoFocus value={pin} onChange={(e) => setPin(e.target.value)}
        placeholder="PIN nuevo" className={`${inputClass} text-center tracking-widest`}
      />
      <button
        type="submit"
        disabled={guardando || pin.length < 4}
        className="w-full flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all"
      >
        {guardando ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
        Guardar y desbloquear
      </button>
    </form>
  );
}

function ConfigurarPinModal({ onClose, onGuardado }: { onClose: () => void; onGuardado: () => void }) {
  const [pin, setPin] = useState('');
  const [confirmar, setConfirmar] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (pin.length < 4) { setError('El PIN debe tener al menos 4 caracteres.'); return; }
    if (pin !== confirmar) { setError('Los PIN no coinciden.'); return; }

    setGuardando(true);
    try {
      await guardarPinMiEspacio(pin);
      onGuardado();
    } catch (err: any) {
      setError(err.message || 'Error al guardar el PIN.');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-sm shadow-2xl p-5 space-y-4">
        <div className="text-center space-y-1">
          <Lock className="w-6 h-6 mx-auto text-blue-400" />
          <h3 className="text-base font-medium text-white">Proteger Mi Espacio con PIN</h3>
          <p className="text-xs text-gray-500">Vas a necesitar este PIN cada vez que entres a esta sección. Si lo olvidás, podés definir uno nuevo confirmando tu contraseña.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-2.5 rounded-lg text-xs">{error}</div>}
          <input type="password" autoFocus value={pin} onChange={(e) => setPin(e.target.value)} placeholder="Nuevo PIN" className={`${inputClass} text-center tracking-widest`} />
          <input type="password" value={confirmar} onChange={(e) => setConfirmar(e.target.value)} placeholder="Confirmar PIN" className={`${inputClass} text-center tracking-widest`} />
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white">Cancelar</button>
            <button
              type="submit"
              disabled={guardando}
              className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all"
            >
              {guardando ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              Activar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
